import clsx from "clsx";
import React from "react";
import { MdBookmark, MdBookmarkBorder } from "react-icons/md";
import { Button, type ButtonProps } from "../Button";
import styles from "./bookmark-button.module.scss";

export interface BookmarkButtonProps extends Omit<ButtonProps, "children"> {
  isBookmarked: boolean;
  onToggle: () => void;
  showLabel?: boolean;
  label?: {
    bookmarked: string;
    unbookmarked: string;
  };
}

export const BookmarkButton = React.forwardRef<
  HTMLButtonElement,
  BookmarkButtonProps
>(
  (
    {
      isBookmarked,
      onToggle,
      showLabel = false,
      label = { bookmarked: "Bookmarked", unbookmarked: "Bookmark" },
      size = "medium",
      variant = "ghost",
      loading = false,
      className,
      ...props
    },
    ref
  ) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!loading) {
        onToggle();
      }
    };

    const buttonClass = clsx(
      styles.bookmarkButton,
      {
        [styles.bookmarked]: isBookmarked,
      },
      className
    );

    const currentLabel = isBookmarked ? label.bookmarked : label.unbookmarked;

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        loading={loading}
        onClick={handleClick}
        className={buttonClass}
        title={currentLabel}
        aria-label={
          showLabel ? "" : isBookmarked ? label.bookmarked : label.unbookmarked
        }
        {...props}
      >
        {isBookmarked ? (
          <MdBookmark className={styles.icon} title={label.bookmarked} />
        ) : (
          <MdBookmarkBorder
            className={styles.icon}
            title={label.unbookmarked}
          />
        )}
        {showLabel && (
          <span>{isBookmarked ? label.bookmarked : label.unbookmarked}</span>
        )}
      </Button>
    );
  }
);

BookmarkButton.displayName = "BookmarkButton";
